import { styled } from '@linaria/react';
import { ArrowLeft, ArrowRight, RotateCw } from 'lucide-react';

import useBrowser from '@/hooks/use-browser';

import OuterCorner from '@/components/OuterCorner';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  tabName?: string;
  url?: string;
  maxHeight?: string;
  // NOTE: The way I implemented the override, I think it only works for overriding it to Dark Mode. I don't think this'll let a window be Light when the page is in Dark Mode.
  colorModeOverride?: 'light' | 'dark';
}

function FakeBrowserWindow({
  tabName = 'Some Example Website',
  url = 'https://www.joshwcomeau.com/example-website',
  maxHeight,
  children,
  colorModeOverride,
  style = {},
  ...delegated
}: Props) {
  const browser = useBrowser();
  return (
    <Wrapper
      {...delegated}
      data-color-mode={colorModeOverride}
      style={{
        // Ugh, so there’s a bug in Firefox that breaks `backdrop-filter` when an element has both `overflow: hidden` and `border-radius`. I don't want to remove the `overflow: hidden`, since it causes the corners to get erased by the overflowing backgrounds. So instead, on Firefox, we’ll straighten the corners.
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1803813
        overflow: browser === 'firefox' ? undefined : 'hidden',
        ...style,
      }}
    >
      <Header>
        <TabBar>
          <TrafficLights>
            <Red />
            <Yellow />
            <Green />
          </TrafficLights>
          <Tab>
            <LeftCorner orientation="bottom-right" />
            <span>{tabName}</span>
            <RightCorner orientation="bottom-left" />
          </Tab>
        </TabBar>
        <AddressBar>
          <FakeBtn aria-hidden="true">
            <ArrowLeft size={14} strokeWidth={2.5} />
          </FakeBtn>
          <FakeBtn aria-hidden="true">
            <ArrowRight size={14} strokeWidth={2.5} />
          </FakeBtn>
          <FakeBtn aria-hidden="true">
            <RotateCw size={14} strokeWidth={2.5} />
          </FakeBtn>
          <Address>
            <span>{url}</span>
          </Address>
        </AddressBar>
      </Header>
      <div
        style={{
          maxHeight,
          overflow: typeof maxHeight === 'string' ? 'auto' : 'clip',
        }}
      >
        {children}
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --window-background-color: hsl(213deg 90% 90%);
  --window-foreground-color: white;
  --window-address-color: hsl(213deg 50% 94%);
  --window-button-color: var(--color-gray-400);
  --window-lights-off: hsl(213deg 40% 80%);
  --red: hsl(3deg 90% 60%);
  --yellow: hsl(41deg 90% 55%);
  --green: hsl(128deg 65% 48%);
  border-radius: 8px;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.05),
    0px 2px 4px hsl(0deg 0% 0% / 0.05),
    0px 8px 16px hsl(0deg 0% 0% / 0.05),
    0px 32px 64px hsl(0deg 0% 0% / 0.125);

  html[data-color-mode='dark'] &,
  &[data-color-mode='dark'] {
    border: 1px solid var(--color-gray-500);
    box-shadow: none;
    --window-background-color: var(--color-gray-200);
    --window-foreground-color: var(--color-gray-300);
    --window-address-color: var(--window-background-color);
    --window-button-color: var(--color-gray-700);
    --window-lights-off: var(--window-foreground-color);
    --red: hsl(3deg 100% 67%);
    --yellow: hsl(41deg 100% 60%);
    --green: hsl(128deg 65% 48%);
  }
`;

const Header = styled.div`
  background: var(--window-background-color);
  border-radius: 7px 7px 0 0;
`;

const TabBar = styled.div`
  display: flex;
  gap: 16px;
  padding-inline: 16px;
  height: 2.25rem;
`;

const Tab = styled.div`
  align-self: flex-end;
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  height: calc(100% - 8px);
  padding-inline: 16px;
  font-size: 0.75rem;
  border-radius: 8px 8px 0 0;
  background-color: var(--window-foreground-color);
  user-select: none;

  span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`;

const TrafficLights = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const TrafficLight = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--window-lights-off);
  @media (hover: hover) and (pointer: fine) {
    transition: background-color 300ms;

    ${Wrapper}:hover & {
      transition: none;
    }
  }
`;
const Red = styled(TrafficLight)`
  @media (hover: hover) and (pointer: fine) {
    transition-delay: 150ms;

    ${Wrapper}:hover & {
      background-color: var(--red);
    }
  }
`;
const Yellow = styled(TrafficLight)`
  @media (hover: hover) and (pointer: fine) {
    transition-delay: 75ms;

    ${Wrapper}:hover & {
      background-color: var(--yellow);
    }
  }
`;
const Green = styled(TrafficLight)`
  @media (hover: hover) and (pointer: fine) {
    ${Wrapper}:hover & {
      background-color: var(--green);
    }
  }
`;

const AddressBar = styled.div`
  display: flex;
  align-items: center;
  padding-right: 16px;
  height: 2.25rem;
  background-color: var(--window-foreground-color);
  border-radius: 8px 8px 0 0;
`;

const FakeBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  color: var(--window-button-color);
`;
const Address = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  height: 1.5rem;
  margin-left: 16px;
  border-radius: 1000px;
  font-size: 0.75rem;
  padding-inline: 10px;
  background-color: var(--window-address-color);

  span {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Corner = styled(OuterCorner)`
  position: absolute;
  bottom: 0;
  width: 8px;
  height: 8px;
  color: var(--window-foreground-color);
`;

const LeftCorner = styled(Corner)`
  left: 0;
  transform: translateX(-100%);
`;
const RightCorner = styled(Corner)`
  position: absolute;
  right: 0;
  bottom: 0;
  transform: translateX(100%);
`;
export default FakeBrowserWindow;
